import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/formatters';
import {
  VESTING_BAR_CLASSES,
  formatShare,
  type VestingState,
} from './carryFundValues';

interface VestingStateBadgeProps {
  state: VestingState;
  // Vesting is the usual state, so it shows only where it is asked for.
  vestingLabel?: string;
}

export function VestingStateBadge({
  state,
  vestingLabel,
}: VestingStateBadgeProps) {
  if (state === 'FROZEN') return <Badge variant="secondary">Frozen</Badge>;
  const label = state === 'FULLY_VESTED' ? 'Fully vested' : vestingLabel;
  if (state === 'NO_PERIOD' || !label) return null;
  return (
    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300">
      {label}
    </Badge>
  );
}

interface CarryVestingBarProps {
  state: VestingState;
  share: number;
  startDate: string | null;
  endDate: string | null;
  note: string;
}

export function CarryVestingBar({
  state,
  share,
  startDate,
  endDate,
  note,
}: CarryVestingBarProps) {
  if (state === 'NO_PERIOD') {
    return (
      <p className="text-sm text-muted-foreground">
        No vesting period set for this fund yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-medium">
          Vesting
          <VestingStateBadge state={state} />
        </span>
        <span className="text-sm font-medium">{formatShare(share)}</span>
      </div>
      <Progress
        value={share * 100}
        className={cn('h-2', VESTING_BAR_CLASSES[state])}
      />
      <div className="flex justify-between gap-4 text-xs text-muted-foreground">
        <span>{formatDate(startDate)}</span>
        <span className="text-center">{note}</span>
        <span>{formatDate(endDate)}</span>
      </div>
    </div>
  );
}
